/**
 * IoT event payload schemas — IOT-EVENT-SUBSTRATE-1.
 * Eight canonical event types published by the IoT module to the Kairos EventBus.
 *
 * Usage: validate incoming/outgoing payloads via IotEventPayload before calling
 * EventBusService.publish() or publishIotEvent().
 */
import { z } from 'zod';

const IotSensorReading = z.object({
  sensorId:    z.string(),
  sensorType:  z.enum(['temperature', 'humidity', 'door', 'tilt', 'shock', 'battery', 'gps', 'signal']),
  value:       z.number(),
  unit:        z.string(),
  observedAt:  z.string(), // ISO-8601
});

// ── 1. iot.breach.detected ────────────────────────────────────────────────────

export const IotBreachDetectedPayload = z.object({
  shipmentId:          z.string(),
  sensorReading:       IotSensorReading,
  breachType:          z.enum(['threshold-exceeded', 'threshold-undershot']),
  severity:            z.enum(['advisory', 'warning', 'critical']),
  setpointMin:         z.number().nullable(),
  setpointMax:         z.number().nullable(),
  consecutiveSamples:  z.number().int().nonnegative(),
});

// ── 2. iot.tamper.suspected ───────────────────────────────────────────────────

export const IotTamperSuspectedPayload = z.object({
  shipmentId:   z.string(),
  sensorIds:    z.array(z.string()),
  confidence:   z.number().min(0).max(1),
  factorsCited: z.array(z.string()),
  suspectedAt:  z.string(), // ISO-8601
});

// ── 3. iot.geofence.entered ───────────────────────────────────────────────────

export const IotGeofenceEnteredPayload = z.object({
  shipmentId:   z.string(),
  geofenceId:   z.string(),
  geofenceName: z.string(),
  lat:          z.number(),
  lng:          z.number(),
  enteredAt:    z.string(), // ISO-8601
});

// ── 4. iot.geofence.exited ────────────────────────────────────────────────────

export const IotGeofenceExitedPayload = z.object({
  shipmentId:   z.string(),
  geofenceId:   z.string(),
  geofenceName: z.string(),
  lat:          z.number(),
  lng:          z.number(),
  exitedAt:     z.string(), // ISO-8601
  dwellMinutes: z.number().nonnegative(),
});

// ── 5. iot.dwell.exceeded ─────────────────────────────────────────────────────

export const IotDwellExceededPayload = z.object({
  shipmentId:      z.string(),
  locationId:      z.string(),
  locationName:    z.string(),
  dwellMinutes:    z.number().nonnegative(),
  thresholdMinutes: z.number().nonnegative(),
  severity:        z.enum(['advisory', 'warning', 'critical']),
});

// ── 6. iot.battery.critical ───────────────────────────────────────────────────

export const IotBatteryCriticalPayload = z.object({
  shipmentId:     z.string(),
  deviceId:       z.string(),
  batteryPercent: z.number().min(0).max(100),
  estimatedHours: z.number().nonnegative().nullable(),
});

// ── 7. iot.signal.lost ────────────────────────────────────────────────────────

export const IotSignalLostPayload = z.object({
  shipmentId:       z.string(),
  deviceId:         z.string(),
  lastKnownLat:     z.number().nullable(),
  lastKnownLng:     z.number().nullable(),
  lastContactAt:    z.string(), // ISO-8601
  silenceMinutes:   z.number().nonnegative(),
});

// ── 8. iot.threshold.predicted ────────────────────────────────────────────────
// AEGIS-derived — confidence inference wrapped via withCostTracking (COST-1).

export const IotThresholdPredictedPayload = z.object({
  shipmentId:      z.string(),
  predictedBreach: z.enum(['threshold-exceeded', 'threshold-undershot']),
  sensorType:      IotSensorReading.shape.sensorType,
  confidence:      z.number().min(0).max(1),
  factorsCited:    z.array(z.string()),
  estimatedEtaMin: z.number().nonnegative(), // minutes until predicted breach
  severity:        z.enum(['advisory', 'warning', 'critical']),
});

// ── Discriminated union for runtime validation ────────────────────────────────

export const IOT_EVENT_TYPES = /** @type {const} */ ([
  'iot.breach.detected',
  'iot.tamper.suspected',
  'iot.geofence.entered',
  'iot.geofence.exited',
  'iot.dwell.exceeded',
  'iot.battery.critical',
  'iot.signal.lost',
  'iot.threshold.predicted',
]);

export const IotEventPayload = z.discriminatedUnion('eventType', [
  z.object({ eventType: z.literal('iot.breach.detected'),    payload: IotBreachDetectedPayload }),
  z.object({ eventType: z.literal('iot.tamper.suspected'),   payload: IotTamperSuspectedPayload }),
  z.object({ eventType: z.literal('iot.geofence.entered'),   payload: IotGeofenceEnteredPayload }),
  z.object({ eventType: z.literal('iot.geofence.exited'),    payload: IotGeofenceExitedPayload }),
  z.object({ eventType: z.literal('iot.dwell.exceeded'),     payload: IotDwellExceededPayload }),
  z.object({ eventType: z.literal('iot.battery.critical'),   payload: IotBatteryCriticalPayload }),
  z.object({ eventType: z.literal('iot.signal.lost'),        payload: IotSignalLostPayload }),
  z.object({ eventType: z.literal('iot.threshold.predicted'), payload: IotThresholdPredictedPayload }),
]);

/**
 * @typedef {import('zod').infer<typeof IotBreachDetectedPayload>}    IotBreachDetectedPayloadType
 * @typedef {import('zod').infer<typeof IotTamperSuspectedPayload>}   IotTamperSuspectedPayloadType
 * @typedef {import('zod').infer<typeof IotGeofenceEnteredPayload>}   IotGeofenceEnteredPayloadType
 * @typedef {import('zod').infer<typeof IotGeofenceExitedPayload>}    IotGeofenceExitedPayloadType
 * @typedef {import('zod').infer<typeof IotDwellExceededPayload>}     IotDwellExceededPayloadType
 * @typedef {import('zod').infer<typeof IotBatteryCriticalPayload>}   IotBatteryCriticalPayloadType
 * @typedef {import('zod').infer<typeof IotSignalLostPayload>}        IotSignalLostPayloadType
 * @typedef {import('zod').infer<typeof IotThresholdPredictedPayload>} IotThresholdPredictedPayloadType
 */
